import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  FileText, Shield, CheckCircle, Printer, Download, 
  AlertTriangle, CreditCard, Wallet, ExternalLink, RefreshCw,
  Scale, FileCheck, Package, Layers, Mail, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ethers } from 'ethers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generateOfficialUCC1 } from '../utils/documentTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address owner) view returns (uint256)"
];

const CreditEnhancement = () => {
  const { user, token } = useAuth();
  const { account, bethBalance, connectWallet } = useWeb3();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(false);
  const [treasurySummary, setTreasurySummary] = useState(null);
  const [guarantees, setGuarantees] = useState([]);
  const [tokenizedChecks, setTokenizedChecks] = useState([]);
  const [uccFilings, setUccFilings] = useState([]);
  
  // Form states
  const [reportForm, setReportForm] = useState({
    borrower_name: '',
    borrower_wallet: '',
    lender_name: '',
    beth_collateral: ''
  });
  
  const [pasForm, setPasForm] = useState({
    borrower_name: '',
    borrower_wallet: '',
    lender_name: '',
    lender_contact: '',
    original_debt: '',
    outstanding_balance: '',
    account_number: '',
    beth_collateral: '',
    governing_law: 'State of Delaware, USA'
  });

  const [checkForm, setCheckForm] = useState({
    payee_name: '',
    payee_wallet: '',
    amount: '',
    memo: '',
    linked_report_id: ''
  });

  const [uccForm, setUccForm] = useState({
    debtor_name: '',
    debtor_address: '',
    debtor_wallet: '',
    debtor_city: '',
    debtor_state: 'TN',
    debtor_zip: '',
    secured_party_name: '',
    secured_party_address: '',
    secured_party_city: '',
    secured_party_state: 'TN',
    secured_party_zip: '',
    filing_state: 'Tennessee',
    agreement_date: new Date().toISOString().split('T')[0],
    beth_collateral: '',
    max_indebtedness: '0.00'
  });
  
  // E-signature modal state
  const [showEsignModal, setShowEsignModal] = useState(false);
  const [esignEmail, setEsignEmail] = useState('');
  const [esignDocType, setEsignDocType] = useState('');
  const [esignDocData, setEsignDocData] = useState(null);
  const [sendingEsign, setSendingEsign] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const summaryRes = await fetch(`${API}/credit-enhancement/treasury-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (summaryRes.ok) setTreasurySummary(await summaryRes.json());
    } catch (e) { console.log('Summary fetch error'); }

    try {
      const guaranteesRes = await fetch(`${API}/credit-enhancement/guarantees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (guaranteesRes.ok) {
        const data = await guaranteesRes.json();
        setGuarantees(data.guarantees || []);
      }
    } catch (e) { console.log('Guarantees fetch error'); }

    try {
      const checksRes = await fetch(`${API}/tokenized-checks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (checksRes.ok) {
        const data = await checksRes.json();
        setTokenizedChecks(data.checks || []);
      }
    } catch (e) { console.log('Checks fetch error'); }

    try {
      const uccRes = await fetch(`${API}/ucc-filings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (uccRes.ok) {
        const data = await uccRes.json();
        setUccFilings(data.filings || []);
      }
    } catch (e) { console.log('UCC fetch error'); }
  };

  const createReport = async (e) => {
    e.preventDefault();
    if (!reportForm.borrower_name || !reportForm.borrower_wallet || !reportForm.lender_name || !reportForm.beth_collateral) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        borrower_name: reportForm.borrower_name,
        borrower_wallet: reportForm.borrower_wallet,
        lender_name: reportForm.lender_name,
        beth_collateral: reportForm.beth_collateral
      });
      
      const res = await fetch(`${API}/credit-enhancement/report?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Credit Enhancement Report created successfully!');
        
        // Auto-fill ALL related forms with report data
        // 1. Pre-fill UCC form
        setUccForm(prev => ({
          ...prev,
          debtor_name: reportForm.borrower_name,
          debtor_address: '',
          debtor_wallet: reportForm.borrower_wallet,
          secured_party_name: reportForm.lender_name,
          secured_party_address: '',
          filing_state: 'Delaware',
          agreement_date: new Date().toISOString().split('T')[0],
          beth_collateral: reportForm.beth_collateral,
          max_indebtedness: (parseFloat(reportForm.beth_collateral) * 1.00).toFixed(2)
        }));
        
        // 2. Pre-fill PAS Agreement form
        setPasForm(prev => ({
          ...prev,
          borrower_name: reportForm.borrower_name,
          borrower_wallet: reportForm.borrower_wallet,
          lender_name: reportForm.lender_name,
          beth_collateral: reportForm.beth_collateral
        }));
        
        // 3. Pre-fill Tokenized Check form with report reference
        setCheckForm(prev => ({
          ...prev,
          payee_name: reportForm.lender_name,
          amount: reportForm.beth_collateral,
          linked_report_id: data.report?.report_id || data.guarantee_id || '',
          memo: `Credit Enhancement - ${reportForm.borrower_name}`
        }));
        
        // Clear the report form
        const createdReport = data.report || { 
          borrower_name: reportForm.borrower_name,
          borrower_wallet: reportForm.borrower_wallet,
          lender_name: reportForm.lender_name,
          internal_beth_collateral: parseFloat(reportForm.beth_collateral),
          report_id: data.report?.report_id || data.guarantee_id
        };
        
        setReportForm({ borrower_name: '', borrower_wallet: '', lender_name: '', beth_collateral: '' });
        await fetchData();
        
        // Ask user if they want to print immediately
        toast.info('Forms auto-filled! Click PAS, UCC-1, or Tokenized Checks tabs to continue.', { duration: 5000 });
        
        // Automatically open print dialog for the created report
        setTimeout(() => {
          printDocument('report', createdReport);
        }, 500);
        
      } else {
        toast.error(data.error || 'Failed to create report');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating report');
    } finally {
      setLoading(false);
    }
  };

  const createPAS = async (e) => {
    e.preventDefault();
    if (!pasForm.borrower_name || !pasForm.borrower_wallet || !pasForm.lender_name || !pasForm.beth_collateral) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        borrower_name: pasForm.borrower_name,
        borrower_wallet: pasForm.borrower_wallet,
        lender_name: pasForm.lender_name,
        lender_contact: pasForm.lender_contact || '',
        original_debt: pasForm.original_debt || '0',
        outstanding_balance: pasForm.outstanding_balance || '0',
        account_number: pasForm.account_number || '',
        beth_collateral: pasForm.beth_collateral,
        governing_law: pasForm.governing_law
      });
      
      const res = await fetch(`${API}/credit-enhancement/pas-agreement?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('PAS Agreement created successfully!');
        
        // Auto-fill ALL related forms
        // 1. Pre-fill UCC form
        setUccForm(prev => ({
          ...prev,
          debtor_name: pasForm.borrower_name,
          debtor_address: '',
          debtor_wallet: pasForm.borrower_wallet,
          secured_party_name: pasForm.lender_name,
          secured_party_address: '',
          filing_state: 'Delaware',
          agreement_date: new Date().toISOString().split('T')[0],
          beth_collateral: pasForm.beth_collateral,
          max_indebtedness: (parseFloat(pasForm.beth_collateral) * 1.00).toFixed(2)
        }));
        
        // 2. Pre-fill Tokenized Check form
        setCheckForm(prev => ({
          ...prev,
          payee_name: pasForm.lender_name,
          amount: pasForm.beth_collateral,
          linked_report_id: data.agreement?.agreement_id || data.guarantee_id || '',
          memo: `PAS Settlement - ${pasForm.borrower_name}`
        }));
        
        // Store PAS data for printing
        const createdPAS = data.agreement || {
          borrower_name: pasForm.borrower_name,
          borrower_wallet: pasForm.borrower_wallet,
          lender_name: pasForm.lender_name,
          beth_collateral: parseFloat(pasForm.beth_collateral),
          agreement_id: data.agreement?.agreement_id || data.guarantee_id
        };
        
        // Clear form
        setPasForm({
          borrower_name: '', borrower_wallet: '', lender_name: '', lender_contact: '',
          original_debt: '', outstanding_balance: '', account_number: '', beth_collateral: '',
          governing_law: 'State of Delaware, USA'
        });
        
        await fetchData();
        
        toast.info('Forms auto-filled! Click UCC-1 or Tokenized Checks tabs to continue.', { duration: 5000 });
        
        // Automatically open print dialog
        setTimeout(() => {
          printDocument('pas', createdPAS);
        }, 500);
        
      } else {
        toast.error(data.error || 'Failed to create agreement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating agreement');
    } finally {
      setLoading(false);
    }
  };

  const createTokenizedCheck = async (e) => {
    e.preventDefault();
    if (!checkForm.payee_name || !checkForm.payee_wallet || !checkForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/tokenized-checks/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          payee_name: checkForm.payee_name,
          payee_wallet: checkForm.payee_wallet,
          amount: parseFloat(checkForm.amount),
          memo: checkForm.memo,
          linked_report_id: checkForm.linked_report_id || null
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Tokenized Check #${data.check.check_number} created!`);
        // Immediately update the local state with the new check for instant UI feedback
        setTokenizedChecks(prev => [data.check, ...prev]);
        
        // Store check data for printing
        const createdCheck = data.check;
        
        setCheckForm({ payee_name: '', payee_wallet: '', amount: '', memo: '', linked_report_id: '' });
        // Also refresh from server to ensure consistency
        await fetchData();
        
        // Automatically open print dialog
        setTimeout(() => {
          printDocument('check', createdCheck);
        }, 500);
        
      } else {
        toast.error(data.error || 'Failed to create check');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating tokenized check');
    } finally {
      setLoading(false);
    }
  };

  const createUCCFiling = async (e) => {
    e.preventDefault();
    if (!uccForm.debtor_name || !uccForm.secured_party_name || !uccForm.beth_collateral) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/ucc-filings/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(uccForm)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`UCC-1 Filing ${data.filing.filing_number} created!`);
        // Immediately update the local state for instant UI feedback
        setUccFilings(prev => [data.filing, ...prev]);
        
        // Store filing data for printing
        const createdFiling = data.filing;
        
        setUccForm({
          debtor_name: '', debtor_address: '', debtor_wallet: '',
          secured_party_name: '', secured_party_address: '',
          filing_state: 'Delaware', agreement_date: new Date().toISOString().split('T')[0],
          beth_collateral: '', max_indebtedness: '0.00'
        });
        
        // Also refresh from server to ensure consistency
        await fetchData();
        
        // Automatically open print dialog for UCC-1
        setTimeout(() => {
          printDocument('ucc', createdFiling);
        }, 500);
        
      } else {
        toast.error(data.error || 'Failed to create UCC filing');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating UCC filing');
    } finally {
      setLoading(false);
    }
  };

  const issueCheck = async (checkId) => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    const check = tokenizedChecks.find(c => c.check_id === checkId);
    if (!check) return;
    
    setLoading(true);
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const bethContract = new ethers.Contract(BETH_TOKEN, ERC20_ABI, signer);
      
      const amountWei = ethers.utils.parseUnits(check.amount.toString(), 18);
      
      toast.info('Transferring BETH... Please confirm in MetaMask');
      const tx = await bethContract.transfer(check.payee_wallet, amountWei);
      
      toast.info('Transaction submitted. Waiting for confirmation...');
      await tx.wait();
      
      await fetch(`${API}/tokenized-checks/${checkId}/issue`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ tx_hash: tx.hash })
      });
      
      toast.success('Check issued successfully!');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      if (error.code === 'ACTION_REJECTED') {
        toast.error('Transaction cancelled');
      } else {
        toast.error('Failed to issue check');
      }
    } finally {
      setLoading(false);
    }
  };

  // Pre-fill UCC from guarantee
  const prefillUccFromGuarantee = (guarantee) => {
    setUccForm({
      debtor_name: guarantee.borrower_name || '',
      debtor_address: '',
      debtor_wallet: guarantee.borrower_wallet || '',
      debtor_city: '',
      debtor_state: 'TN',
      debtor_zip: '',
      secured_party_name: guarantee.lender_name || '',
      secured_party_address: '',
      secured_party_city: '',
      secured_party_state: 'TN',
      secured_party_zip: '',
      filing_state: 'Tennessee',
      agreement_date: guarantee.created_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      beth_collateral: guarantee.beth_amount?.toString() || '',
      max_indebtedness: '0.00'
    });
    setActiveTab('ucc');
    toast.info('UCC-1 form pre-filled. Review and submit.');
  };
  
  // Send E-Signature Request via Email
  const sendEsignatureRequest = async () => {
    if (!esignEmail || !esignDocType || !esignDocData) {
      toast.error('Please provide recipient email and document');
      return;
    }
    
    setSendingEsign(true);
    try {
      const res = await fetch(`${API}/esignature-workflow/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          document_name: `${esignDocType.toUpperCase()} - ${esignDocData.filing_number || esignDocData.guarantee_id || esignDocData.check_number || 'Document'}`,
          agreement_type: esignDocType,
          recipients: [
            {
              name: esignDocData.debtor_name || esignDocData.borrower_name || esignDocData.payee_name || 'Signer',
              email: esignEmail,
              role: 'debtor'
            }
          ],
          agreement_id: esignDocData.filing_number || esignDocData.guarantee_id || esignDocData.check_id
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`E-signature request sent to ${esignEmail}`);
        setShowEsignModal(false);
        setEsignEmail('');
        setEsignDocType('');
        setEsignDocData(null);
      } else {
        toast.error(data.error || 'Failed to send e-signature request');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending e-signature request');
    } finally {
      setSendingEsign(false);
    }
  };
  
  // Open E-Sign modal for a document
  const openEsignModal = (docType, docData) => {
    setEsignDocType(docType);
    setEsignDocData(docData);
    setEsignEmail(docData.debtor_email || docData.borrower_email || user?.email || '');
    setShowEsignModal(true);
  };

  const printDocument = (type, data) => {
    try {
      const printContent = generatePrintableDocument(type, data);
      
      // Try to open a new window
      const printWindow = window.open('', '_blank', 'width=850,height=700,scrollbars=yes,resizable=yes');
      
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for content to load then print
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (printError) {
            console.error('Print dialog error:', printError);
            toast.info('Document opened in new window. Use browser print (Ctrl+P / Cmd+P)');
          }
        }, 500);
        
        toast.success('Document opened for printing');
      } else {
        // Popup was blocked - try alternative method
        toast.error('Popup blocked! Trying alternative method...', { duration: 2000 });
        
        // Create a hidden iframe as fallback
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow || iframe.contentDocument;
        if (iframeDoc.document) {
          iframeDoc.document.write(printContent);
          iframeDoc.document.close();
          
          setTimeout(() => {
            try {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            } catch (e) {
              toast.error('Please allow popups for this site to print documents');
            }
            // Remove iframe after printing
            setTimeout(() => document.body.removeChild(iframe), 1000);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Error generating print document. Please try again.');
    }
  };

  // Document Package Generator - Creates complete Credit Enhancement bundle with TIPO integration
  const generateDocumentPackage = async (data) => {
    setLoading(true);
    toast.info('Generating complete document package with TIPO data...', { duration: 3000 });
    
    try {
      // Fetch TIPO orders for this user
      let tipoOrders = [];
      try {
        const tipoRes = await fetch(`${API}/treasury-securities/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (tipoRes.ok) {
          const tipoData = await tipoRes.json();
          tipoOrders = tipoData.orders || [];
        }
      } catch (e) {
        console.log('Could not fetch TIPO orders:', e);
      }
      
      // Generate all document numbers for consistency
      const packageId = `PKG-${Date.now().toString(36).toUpperCase()}`;
      const timestamp = Date.now().toString(36).toUpperCase();
      const random = () => Math.random().toString(36).substring(2, 6).toUpperCase();
      
      const instrumentNumbers = {
        report: `CER-${timestamp}-${random()}`,
        pas: `PAS-${timestamp}-${random()}`,
        ucc: `UCC-${timestamp}-${random()}`,
        check: `TCK-${timestamp}-${random()}`,
        checkDisclosure: `TCD-${timestamp}-${random()}`,
        tipo: tipoOrders.length > 0 ? `TIPO-${timestamp}-${random()}` : null
      };
      
      // Create complete data objects
      const reportData = {
        report_id: instrumentNumbers.report,
        borrower_name: data.borrower_name,
        borrower_wallet: data.borrower_wallet,
        lender_name: data.lender_name,
        internal_beth_collateral: parseFloat(data.beth_collateral),
        beth_amount: parseFloat(data.beth_collateral),
        conditional_guarantee_usd: parseFloat(data.beth_collateral) * 1.00
      };
      
      const pasData = {
        agreement_id: instrumentNumbers.pas,
        borrower_name: data.borrower_name,
        borrower_wallet: data.borrower_wallet,
        lender_name: data.lender_name,
        beth_collateral: parseFloat(data.beth_collateral),
        beth_amount: parseFloat(data.beth_collateral),
        governing_law: 'State of Delaware, USA'
      };
      
      const uccData = {
        filing_number: instrumentNumbers.ucc,
        debtor_name: data.borrower_name,
        debtor_wallet: data.borrower_wallet,
        debtor_address: data.borrower_address || '',
        secured_party_name: data.lender_name,
        secured_party_address: data.lender_address || '',
        filing_state: 'Delaware',
        agreement_date: new Date().toISOString().split('T')[0],
        beth_collateral: parseFloat(data.beth_collateral),
        max_indebtedness: (parseFloat(data.beth_collateral) * 1.00).toFixed(2),
        created_at: new Date().toISOString()
      };
      
      const checkData = {
        check_id: instrumentNumbers.check,
        check_number: `CHK${Date.now().toString().slice(-6)}`,
        payee_name: data.lender_name,
        payee_wallet: data.lender_wallet || '0x0000000000000000000000000000000000000000',
        amount: parseFloat(data.beth_collateral),
        memo: `Credit Enhancement Settlement - ${data.borrower_name}`,
        status: 'draft',
        created_at: new Date().toISOString()
      };
      
      // Calculate TIPO totals for the package
      const tipoSummary = {
        total_orders: tipoOrders.length,
        total_principal: tipoOrders.reduce((sum, o) => sum + (o.principal_amount || 0), 0),
        orders: tipoOrders.slice(0, 5) // Include up to 5 most recent orders
      };
      
      // Generate multi-page document with TIPO
      const packageHTML = generateDocumentPackageHTML(packageId, instrumentNumbers, reportData, pasData, uccData, checkData, tipoSummary);
      
      // Create a hidden container for PDF generation
      const container = document.createElement('div');
      container.innerHTML = packageHTML;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      document.body.appendChild(container);
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate PDF using html2canvas and jsPDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pages = container.querySelectorAll('.page');
      
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        // Capture page as canvas
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        // Add to PDF
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        
        if (i > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }
      
      // Clean up
      document.body.removeChild(container);
      
      // Download PDF
      const fileName = `Credit_Enhancement_Package_${packageId}.pdf`;
      pdf.save(fileName);
      
      toast.success(`Document package downloaded as ${fileName}`);
      
    } catch (error) {
      console.error('Package generation error:', error);
      toast.error('Error generating document package. Opening print view instead...');
      
      // Fallback to print window
      try {
        const packageHTML = generateDocumentPackageHTML(
          `PKG-${Date.now().toString(36).toUpperCase()}`,
          { report: 'CER-FALLBACK', pas: 'PAS-FALLBACK', ucc: 'UCC-FALLBACK', check: 'MO-FALLBACK' },
          { borrower_name: data.borrower_name, beth_amount: data.beth_collateral },
          { borrower_name: data.borrower_name, beth_amount: data.beth_collateral },
          { debtor_name: data.borrower_name },
          { payee_name: data.lender_name },
          null
        );
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(packageHTML);
          printWindow.document.close();
        }
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateDocumentPackageHTML = (packageId, instrumentNumbers, reportData, pasData, uccData, checkData, tipoSummary = null) => {
    const watermarkSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" class="watermark-svg">
        <circle cx="100" cy="100" r="95" fill="none" stroke="#0a1628" stroke-width="2"/>
        <circle cx="100" cy="100" r="85" fill="none" stroke="#b8860b" stroke-width="1"/>
        <text x="100" y="90" text-anchor="middle" font-family="Georgia, serif" font-size="14" font-weight="bold" fill="#0a1628">BETHEREUM</text>
        <text x="100" y="110" text-anchor="middle" font-family="Georgia, serif" font-size="8" fill="#b8860b">OFFICIAL</text>
      </svg>
    `;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Credit Enhancement Document Package - ${packageId}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          body { 
            font-family: 'Georgia', serif; 
            margin: 0;
            padding: 0;
            color: #0a1628;
            line-height: 1.5;
          }
          .page { 
            page-break-after: always; 
            padding: 30px;
            position: relative;
            min-height: 10in;
            background: white;
          }
          .page:last-child { page-break-after: auto; }
          .watermark-svg {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            opacity: 0.04;
            width: 400px;
            height: 400px;
            pointer-events: none;
          }
          .header {
            text-align: center;
            border-bottom: 3px double #b8860b;
            padding-bottom: 20px;
            margin-bottom: 25px;
            position: relative;
          }
          .header h1 { color: #0a1628; font-size: 28px; margin: 0; letter-spacing: 4px; }
          .header .subtitle { color: #b8860b; font-size: 10px; letter-spacing: 2px; margin-top: 5px; }
          .seal {
            position: absolute;
            top: 0;
            left: 0;
            width: 55px;
            height: 55px;
            border: 2px solid #0a1628;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .seal span { font-size: 7px; color: #0a1628; font-weight: bold; line-height: 1.2; }
          .instrument-number {
            position: absolute;
            top: 5px;
            right: 5px;
            font-family: 'Courier New', monospace;
            font-size: 9px;
            background: #f0f0f0;
            padding: 4px 8px;
            border: 1px solid #ccc;
          }
          .document-title {
            text-align: center;
            background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%);
            color: white;
            padding: 12px;
            margin: 15px 0;
            font-size: 14px;
            letter-spacing: 2px;
          }
          .section {
            margin: 15px 0;
            padding: 12px;
            background: #fafafa;
            border-left: 4px solid #b8860b;
          }
          .section-title {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px dotted #ddd;
            font-size: 11px;
          }
          .row:last-child { border-bottom: none; }
          .row .label { color: #666; }
          .row .value { font-weight: bold; color: #0a1628; }
          .highlight-box {
            background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%);
            border: 2px solid #b8860b;
            padding: 15px;
            margin: 15px 0;
            text-align: center;
          }
          .highlight-box .amount {
            font-size: 24px;
            font-weight: bold;
            color: #b8860b;
          }
          .signature-section {
            display: flex;
            gap: 20px;
            margin-top: 30px;
          }
          .sig-box {
            flex: 1;
            border: 1px solid #0a1628;
            padding: 15px;
            text-align: center;
          }
          .sig-line {
            border-top: 1px solid #0a1628;
            width: 80%;
            margin: 35px auto 5px auto;
          }
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #0a1628;
            font-size: 8px;
            color: #666;
            text-align: center;
          }
          .legal-text { font-size: 9px; line-height: 1.6; text-align: justify; }
          .cover-page { text-align: center; padding-top: 100px; }
          .cover-page h1 { font-size: 36px; letter-spacing: 6px; margin-bottom: 20px; }
          .cover-page .package-id { font-family: monospace; font-size: 14px; background: #f0f0f0; padding: 10px 20px; display: inline-block; }
          .toc { margin-top: 60px; text-align: left; max-width: 400px; margin-left: auto; margin-right: auto; }
          .toc h3 { font-size: 14px; margin-bottom: 20px; color: #b8860b; }
          .toc-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dotted #ccc; }
          .money-order { 
            width: 100%; 
            max-width: 700px; 
            height: auto;
            margin: 20px auto;
            background: linear-gradient(135deg, #fefefe 0%, #f8f8f0 25%, #fefefe 50%, #f0f8f0 75%, #fefefe 100%);
            border: 3px solid #0a1628;
            position: relative;
            padding: 0;
          }
          .mo-border { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border: 2px solid #b8860b; }
          .mo-header { display: flex; justify-content: space-between; padding: 15px 20px 10px 20px; border-bottom: 2px solid #0a1628; }
          .mo-body { padding: 15px 20px; }
          .mo-footer { display: flex; justify-content: space-between; align-items: end; padding: 10px 20px 15px 20px; }
          .micr-line { font-family: 'Courier New', monospace; font-size: 11px; letter-spacing: 3px; color: #333; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { border: none; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <!-- Cover Page -->
        <div class="page cover-page">
          ${watermarkSVG}
          <div class="seal" style="position: absolute; top: 30px; left: 30px;">
            <span>OFFICIAL</span>
            <span style="font-size: 10px;">✦</span>
            <span>DOCUMENT</span>
          </div>
          <h1>BETHEREUM</h1>
          <p style="color: #b8860b; font-size: 14px; letter-spacing: 3px;">PRIVATE WEALTH MANAGEMENT</p>
          <div style="margin: 60px 0;">
            <h2 style="font-size: 24px; color: #0a1628; border: 2px solid #0a1628; padding: 20px; display: inline-block;">
              CREDIT ENHANCEMENT<br/>DOCUMENT PACKAGE
            </h2>
          </div>
          <div class="package-id">PACKAGE ID: ${packageId}</div>
          <p style="margin-top: 20px; color: #666; font-size: 12px;">Generated: ${new Date().toLocaleString()}</p>
          
          <div class="toc">
            <h3>CONTENTS</h3>
            <div class="toc-item"><span>1. Credit Enhancement Report</span><span>${instrumentNumbers.report}</span></div>
            <div class="toc-item"><span>2. Private Accord & Satisfaction</span><span>${instrumentNumbers.pas}</span></div>
            <div class="toc-item"><span>3. UCC-1 Financing Statement</span><span>${instrumentNumbers.ucc}</span></div>
            <div class="toc-item"><span>4. Tokenized Check</span><span>${instrumentNumbers.check}</span></div>
            <div class="toc-item"><span>5. Tokenized Check Issuance & Disclosure Agreement</span><span>${instrumentNumbers.checkDisclosure}</span></div>
            ${tipoSummary && tipoSummary.total_orders > 0 ? `<div class="toc-item"><span>6. Treasury-Indexed Obligations Schedule</span><span>${instrumentNumbers.tipo}</span></div>` : ''}
          </div>
          
          <div style="position: absolute; bottom: 40px; left: 0; right: 0; text-align: center; font-size: 10px; color: #666;">
            <p>🔒 SECURED DOCUMENT PACKAGE • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN}</p>
          </div>
        </div>
        
        <!-- Page 1: Credit Enhancement Report -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>SEAL</span>
            </div>
            <div class="instrument-number">INSTRUMENT: ${instrumentNumbers.report}</div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          </div>
          
          <div class="document-title">CREDIT ENHANCEMENT REPORT</div>
          <p style="text-align: center; font-size: 10px; color: #666;">Document 1 of 4 • Package: ${packageId}</p>
          
          <div class="section">
            <div class="section-title">Debtor / Borrower Information</div>
            <div class="row"><span class="label">Name</span><span class="value">${reportData.borrower_name}</span></div>
            <div class="row"><span class="label">Wallet Address</span><span class="value" style="font-family: monospace; font-size: 10px;">${reportData.borrower_wallet}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Secured Party / Lender</div>
            <div class="row"><span class="label">Name</span><span class="value">${reportData.lender_name}</span></div>
          </div>
          
          <div class="highlight-box">
            <div style="font-size: 10px; color: #666; letter-spacing: 1px; margin-bottom: 5px;">BETH COLLATERAL COMMITTED</div>
            <div class="amount">${reportData.internal_beth_collateral.toLocaleString()} BETH</div>
            <div style="font-size: 11px; color: #0a1628; margin-top: 5px;">USD Equivalent: $${reportData.conditional_guarantee_usd.toLocaleString()}</div>
          </div>
          
          <div style="background: #f9f9f9; padding: 12px; margin: 15px 0; border: 1px solid #ddd; font-size: 9px;">
            <strong>CONDITIONS:</strong> This report is for informational purposes. Any transaction is conditional upon lender acceptance. All BETH backing remains internal to BETH treasury for audit purposes.
          </div>
          
          <div class="signature-section">
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">Debtor Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${reportData.borrower_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">Secured Party Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${reportData.lender_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
          </div>
          
          <div class="footer">
            <p>🔒 SECURED • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN} • Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        
        <!-- Page 2: PAS Agreement -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>SEAL</span>
            </div>
            <div class="instrument-number">INSTRUMENT: ${instrumentNumbers.pas}</div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          </div>
          
          <div class="document-title">PRIVATE ACCORD & SATISFACTION AGREEMENT</div>
          <p style="text-align: center; font-size: 10px; color: #666;">Document 2 of 4 • Package: ${packageId}</p>
          
          <div class="section">
            <div class="section-title">First Party (Debtor)</div>
            <div class="row"><span class="label">Name</span><span class="value">${pasData.borrower_name}</span></div>
            <div class="row"><span class="label">Wallet</span><span class="value" style="font-family: monospace; font-size: 10px;">${pasData.borrower_wallet}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Second Party (Secured Party/Lender)</div>
            <div class="row"><span class="label">Name</span><span class="value">${pasData.lender_name}</span></div>
          </div>
          
          <div class="highlight-box">
            <div style="font-size: 10px; color: #666; letter-spacing: 1px; margin-bottom: 5px;">SETTLEMENT AMOUNT</div>
            <div class="amount">${pasData.beth_collateral.toLocaleString()} BETH</div>
            <div style="font-size: 11px; color: #0a1628; margin-top: 5px;">In full settlement and satisfaction of all claims</div>
          </div>
          
          <div class="legal-text" style="background: white; padding: 15px; margin: 15px 0; border: 1px solid #0a1628;">
            <p><strong>WHEREAS,</strong> the First Party (Debtor) and Second Party (Secured Party) desire to settle and resolve any and all claims, disputes, or obligations between them;</p>
            <p style="margin-top: 10px;"><strong>NOW, THEREFORE,</strong> in consideration of the mutual covenants herein, the parties agree that upon acceptance and execution of this Agreement, the settlement amount specified above shall constitute full accord and satisfaction of any debts, claims, or obligations.</p>
            <p style="margin-top: 10px;"><strong>GOVERNING LAW:</strong> ${pasData.governing_law}</p>
          </div>
          
          <div class="signature-section">
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">First Party Acceptance</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${pasData.borrower_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">Second Party Acceptance</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${pasData.lender_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
          </div>
          
          <div class="footer">
            <p>🔒 SECURED • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN} • Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        
        <!-- Page 3: UCC-1 Financing Statement -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">⚖</span>
              <span>FILING</span>
            </div>
            <div class="instrument-number">INSTRUMENT: ${instrumentNumbers.ucc}</div>
            <h1 style="font-size: 22px;">UCC FINANCING STATEMENT</h1>
            <p class="subtitle">UNIFORM COMMERCIAL CODE - FORM UCC-1</p>
          </div>
          
          <p style="text-align: center; font-size: 10px; color: #666;">Document 3 of 4 • Package: ${packageId}</p>
          
          <div class="section">
            <div class="section-title">A. Debtor Information</div>
            <div class="row"><span class="label">Debtor Name</span><span class="value">${uccData.debtor_name}</span></div>
            <div class="row"><span class="label">Wallet Address</span><span class="value" style="font-family: monospace; font-size: 10px;">${uccData.debtor_wallet}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">B. Secured Party Information</div>
            <div class="row"><span class="label">Secured Party Name</span><span class="value">${uccData.secured_party_name}</span></div>
          </div>
          
          <div style="border: 2px solid #b8860b; padding: 15px; margin: 15px 0; background: linear-gradient(135deg, #fefefe 0%, #f8f4e8 100%);">
            <div style="font-size: 11px; font-weight: bold; margin-bottom: 10px;">C. COLLATERAL DESCRIPTION</div>
            <p class="legal-text">All BETH tokens (ERC-20) now owned or hereafter acquired by Debtor, including all rights to receive, transfer, or otherwise use such tokens; all rights under the Credit Enhancement/PAS Agreement dated ${uccData.agreement_date}; all tokenized checks or payment instruments; and any substitutions, replacements, or proceeds thereof.</p>
            <div style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed #b8860b;">
              <strong>Max Principal Indebtedness:</strong> $${uccData.max_indebtedness}<br/>
              <strong>BETH Collateral:</strong> ${parseFloat(uccData.beth_collateral).toLocaleString()} BETH
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">D. Filing Information</div>
            <div class="row"><span class="label">Filing State</span><span class="value">${uccData.filing_state}</span></div>
            <div class="row"><span class="label">Agreement Date</span><span class="value">${uccData.agreement_date}</span></div>
            <div class="row"><span class="label">Filing Number</span><span class="value">${uccData.filing_number}</span></div>
          </div>
          
          <div class="signature-section">
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">Debtor Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${uccData.debtor_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 9px; text-transform: uppercase;">Secured Party Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${uccData.secured_party_name}</div>
              <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
            </div>
          </div>
          
          <div class="footer">
            <p>🔒 SECURED FILING • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN}</p>
          </div>
        </div>
        
        <!-- Page 4: Tokenized Check -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>SEAL</span>
            </div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          </div>
          
          <div class="document-title">TOKENIZED CHECK</div>
          <p style="text-align: center; font-size: 10px; color: #666;">Document 4 of 6 • Package: ${packageId}</p>
          
          <div class="money-order">
            <div class="mo-border"></div>
            <div class="mo-header">
              <div style="display: flex; align-items: center; gap: 10px;">
                <div style="width: 45px; height: 45px; border: 2px solid #0a1628; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                  <span style="font-size: 6px; font-weight: bold;">OFFICIAL</span>
                  <span style="font-size: 8px;">✦</span>
                  <span style="font-size: 6px; font-weight: bold;">SEAL</span>
                </div>
                <div>
                  <div style="font-size: 18px; font-weight: bold; letter-spacing: 2px;">BETHEREUM</div>
                  <div style="font-size: 8px; color: #b8860b;">TOKENIZED CHECK</div>
                </div>
              </div>
              <div style="text-align: right; font-size: 10px;">
                <div style="font-family: monospace; font-weight: bold; background: #f0f0f0; padding: 3px 8px; border: 1px solid #ccc;">No. ${checkData.check_number}</div>
                <div style="color: #666; margin-top: 5px;">Date: ${new Date().toLocaleDateString()}</div>
                <div style="color: #999; font-size: 8px; margin-top: 3px;">Instrument: ${instrumentNumbers.check}</div>
              </div>
            </div>
            
            <div class="mo-body">
              <div style="margin-bottom: 10px;">
                <span style="font-size: 9px; color: #666;">PAY TO THE ORDER OF</span>
                <div style="font-size: 16px; font-weight: bold; border-bottom: 1px solid #333; padding: 8px 0; margin-top: 3px;">${checkData.payee_name}</div>
                <div style="font-family: monospace; font-size: 9px; color: #666; margin-top: 3px;">${checkData.payee_wallet}</div>
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; margin: 15px 0;">
                <div style="flex: 1; font-size: 11px; border-bottom: 1px solid #999; padding: 5px 0;">
                  <strong>${numberToWords(checkData.amount)}</strong> BETH ********
                </div>
                <div style="margin-left: 15px; border: 3px solid #0a1628; padding: 8px 15px; background: white; text-align: center;">
                  <div style="font-size: 9px; color: #666;">BETH</div>
                  <div style="font-size: 18px; font-weight: bold;">${checkData.amount.toLocaleString()}</div>
                </div>
              </div>
              
              <div style="background: rgba(184, 134, 11, 0.1); border-left: 3px solid #b8860b; padding: 8px; font-size: 9px;">
                <strong>MEMO:</strong> ${checkData.memo}
              </div>
            </div>
            
            <div class="mo-footer">
              <div class="micr-line">⑆021000021⑆ BETH${checkData.check_number}⑈ ${checkData.check_number.padStart(6, '0')}</div>
              <div style="text-align: right;">
                <div style="border-top: 1px solid #333; width: 180px; margin-bottom: 5px;"></div>
                <div style="font-size: 9px; color: #666;">Authorized Signature</div>
              </div>
            </div>
          </div>
          
          <div style="text-align: center; margin-top: 20px; font-size: 9px; color: #666;">
            <p>This tokenized check is backed by BETH tokens on Ethereum Mainnet</p>
            <p style="font-family: monospace; font-size: 8px;">BETH Contract: ${BETH_TOKEN}</p>
          </div>
          
          <div class="footer">
            <p>🔒 SECURED INSTRUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>Package Complete • Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        
        <!-- Page 5: Tokenized Check Issuance & Disclosure Agreement -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">⚖</span>
              <span>LEGAL</span>
            </div>
            <div class="instrument-number">INSTRUMENT: ${instrumentNumbers.checkDisclosure}</div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          </div>
          
          <div class="document-title">TOKENIZED & DRAFT CHECK ISSUANCE AND DISCLOSURE AGREEMENT</div>
          <p style="text-align: center; font-size: 10px; color: #666; margin-top: 5px;">
            <strong>(Conditional Consideration – Private Credit Transactions)</strong>
          </p>
          <p style="text-align: center; font-size: 9px; color: #999;">Confidential – Disclosure & Acknowledgment • Document 5 of 6 • Package: ${packageId}</p>
          
          <div class="section">
            <div class="section-title">1. PURPOSE</div>
            <p style="font-size: 10px; line-height: 1.6; text-align: justify;">
              This Tokenized & Draft Check Issuance and Disclosure Agreement ("Agreement") governs the issuance, delivery, and use of any 
              <strong>tokenized check, draft check, payment instruction, or similar instrument</strong> provided in connection with a private credit, 
              financing, novation, or settlement transaction.
            </p>
            <p style="font-size: 10px; line-height: 1.6; text-align: justify; margin-top: 8px; background: #fff3cd; padding: 8px; border-radius: 4px;">
              This Agreement exists solely to clarify that such instruments are <strong>conditional evidence of consideration</strong> and <strong>not payment</strong>.
            </p>
          </div>
          
          <div class="section">
            <div class="section-title">2. PARTIES</div>
            <table style="width: 100%; font-size: 10px; border-collapse: collapse;">
              <tr>
                <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa; width: 40%;"><strong>Issuer / Platform Administrator:</strong></td>
                <td style="padding: 6px; border: 1px solid #ddd;">Bethereum Private Wealth Management</td>
              </tr>
              <tr>
                <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Recipient (Dealer / Lender / Mortgagee):</strong></td>
                <td style="padding: 6px; border: 1px solid #ddd;">${pasData.lender_name || '___________________________'}</td>
              </tr>
              <tr>
                <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Related Obligor / Borrower:</strong></td>
                <td style="padding: 6px; border: 1px solid #ddd;">${reportData.borrower_name || '___________________________'}</td>
              </tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">3. NATURE OF THE INSTRUMENT</div>
            <p style="font-size: 10px; line-height: 1.6; margin-bottom: 8px;">Any tokenized check, draft check, or payment instruction (the "Instrument") issued:</p>
            <ul style="font-size: 10px; line-height: 1.6; margin: 0 0 0 20px; padding: 0;">
              <li>Is <strong>non-negotiable</strong></li>
              <li>Is <strong>not legal tender</strong></li>
              <li>Is <strong>not payable on demand</strong></li>
              <li>Does <strong>not</strong> constitute a check under UCC Article 3</li>
              <li>Does <strong>not</strong> represent funds on deposit or escrow</li>
            </ul>
            <p style="font-size: 10px; line-height: 1.6; margin-top: 8px; background: #e8f4fd; padding: 8px; border-radius: 4px;">
              The Instrument functions solely as <strong>conditional consideration</strong> pending execution of definitive agreements.
            </p>
          </div>
          
          <div class="section">
            <div class="section-title">4. CONDITIONS PRECEDENT</div>
            <p style="font-size: 10px; line-height: 1.6; margin-bottom: 8px;">The Instrument has no force or effect unless and until:</p>
            <ol style="font-size: 10px; line-height: 1.6; margin: 0 0 0 20px; padding: 0;">
              <li>All definitive transaction agreements are fully executed</li>
              <li>Any required acceptance or acknowledgment forms are signed</li>
              <li>Any applicable novation or assumption is approved in writing</li>
              <li>Issuer confirms activation or release conditions</li>
            </ol>
            <p style="font-size: 10px; line-height: 1.6; margin-top: 8px; background: #f8d7da; padding: 8px; border-radius: 4px;">
              <strong>Absent satisfaction of these conditions, the Instrument is void.</strong>
            </p>
          </div>
          
          <div class="section">
            <div class="section-title">5. RELATIONSHIP TO PAS</div>
            <p style="font-size: 10px; line-height: 1.6;">Where a Performance Assurance Structure (PAS) exists:</p>
            <ul style="font-size: 10px; line-height: 1.6; margin: 5px 0 0 20px; padding: 0;">
              <li>The Instrument does not trigger PAS remedies</li>
              <li>PAS does not guarantee payment under the Instrument</li>
              <li>PAS remains governed exclusively by its own agreement</li>
            </ul>
          </div>
          
          <div class="section">
            <div class="section-title">6. NO SETTLEMENT OR DISCHARGE</div>
            <p style="font-size: 10px; line-height: 1.6; margin-bottom: 8px;">Delivery of the Instrument:</p>
            <ul style="font-size: 10px; line-height: 1.6; margin: 0 0 0 20px; padding: 0;">
              <li>Does <strong>not</strong> settle, pay, or discharge any debt</li>
              <li>Does <strong>not</strong> obligate funding or lien release</li>
              <li>Does <strong>not</strong> create reliance or expectation of payment</li>
            </ul>
            <p style="font-size: 10px; line-height: 1.6; margin-top: 8px;">
              Any settlement requires a <strong>separate written payoff or modification agreement</strong>.
            </p>
          </div>
          
          <div style="display: flex; gap: 15px;">
            <div class="section" style="flex: 1; margin: 10px 0;">
              <div class="section-title">7. ACCOUNTING & TREATMENT</div>
              <p style="font-size: 9px; line-height: 1.5;">Recipients acknowledge that the Instrument:</p>
              <ul style="font-size: 9px; line-height: 1.5; margin: 3px 0 0 15px; padding: 0;">
                <li>Should not be booked as cash or cash equivalent</li>
                <li>Should not be treated as payment received</li>
                <li>May be retained solely for transaction documentation</li>
              </ul>
            </div>
            <div class="section" style="flex: 1; margin: 10px 0;">
              <div class="section-title">8. NO BANKING, INSURANCE, OR FIDUCIARY RELATIONSHIP</div>
              <p style="font-size: 9px; line-height: 1.5;">No party shall construe this Agreement or the Instrument as creating:</p>
              <ul style="font-size: 9px; line-height: 1.5; margin: 3px 0 0 15px; padding: 0;">
                <li>A banking relationship</li>
                <li>An insurance or surety obligation</li>
                <li>A fiduciary duty</li>
              </ul>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">9. GOVERNING LAW</div>
            <p style="font-size: 10px; line-height: 1.6;">
              This Agreement shall be governed by the laws of the <strong>State of Delaware</strong>, without regard to conflicts principles.
            </p>
          </div>
          
          <div class="section-title" style="margin-top: 15px;">10. SIGNATURES</div>
          <div class="signature-section" style="margin-top: 10px;">
            <div class="sig-box">
              <div style="font-size: 8px; text-transform: uppercase;">Issuer / Platform Administrator</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">Bethereum Private Wealth Management</div>
              <div style="font-size: 9px; margin-top: 3px;">Date: _____________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 8px; text-transform: uppercase;">Recipient (Dealer / Lender / Mortgagee)</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${pasData.lender_name || '___________________________'}</div>
              <div style="font-size: 9px; margin-top: 3px;">Date: _____________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 8px; text-transform: uppercase;">Acknowledged by Borrower (if applicable)</div>
              <div class="sig-line"></div>
              <div style="font-size: 9px;">${reportData.borrower_name || '___________________________'}</div>
              <div style="font-size: 9px; margin-top: 3px;">Date: _____________</div>
            </div>
          </div>
          
          <div class="footer">
            <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN} • Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        
        ${tipoSummary && tipoSummary.total_orders > 0 ? `
        <!-- Page 6: Treasury-Indexed Private Obligations Schedule -->
        <div class="page">
          ${watermarkSVG}
          <div class="header">
            <div class="seal">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>SEAL</span>
            </div>
            <div class="instrument-number">SCHEDULE: ${instrumentNumbers.tipo}</div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          </div>
          
          <div class="document-title">TREASURY-INDEXED PRIVATE OBLIGATIONS (TIPO) SCHEDULE</div>
          <p style="text-align: center; font-size: 10px; color: #666;">Document 6 of 6 • Package: ${packageId}</p>
          
          <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 10px; margin: 15px 0; font-size: 9px; color: #856404;">
            <strong>⚠️ IMPORTANT DISCLOSURE:</strong> Treasury-Indexed Private Obligations (TIPO) are private contractual 
            obligations, NOT U.S. Government securities. Treasury rates are used as a reference index only. These obligations 
            are backed by BETH credit enhancement.
          </div>
          
          <div class="section">
            <div class="section-title">TIPO Holdings Summary</div>
            <div class="row"><span class="label">Holder Name</span><span class="value">${reportData.borrower_name}</span></div>
            <div class="row"><span class="label">Total TIPO Holdings</span><span class="value">${tipoSummary.total_orders} obligation(s)</span></div>
            <div class="row"><span class="label">Total Principal</span><span class="value" style="color: #b8860b; font-size: 14px;">$${tipoSummary.total_principal.toLocaleString()}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">Individual TIPO Holdings</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 10px;">
              <thead>
                <tr style="background: #0a1628; color: white;">
                  <th style="padding: 8px; text-align: left; border: 1px solid #1a2d4a;">Order ID</th>
                  <th style="padding: 8px; text-align: left; border: 1px solid #1a2d4a;">Product</th>
                  <th style="padding: 8px; text-align: right; border: 1px solid #1a2d4a;">Principal</th>
                  <th style="padding: 8px; text-align: right; border: 1px solid #1a2d4a;">APY</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #1a2d4a;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${tipoSummary.orders.map((order, idx) => `
                  <tr style="background: ${idx % 2 === 0 ? '#fafafa' : 'white'};">
                    <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace; font-size: 9px;">${order.order_id}</td>
                    <td style="padding: 8px; border: 1px solid #ddd;">${order.product_name}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">$${(order.principal_amount || 0).toLocaleString()}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: right; color: #b8860b;">${order.apy || 'N/A'}%</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">
                      <span style="background: ${order.status === 'active' ? '#d4edda' : '#ffeeba'}; padding: 2px 6px; border-radius: 4px; font-size: 9px;">
                        ${(order.status || 'pending').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="section" style="background: linear-gradient(135deg, #f0f7ff 0%, #fefefe 100%);">
            <div class="section-title">Credit Enhancement Integration</div>
            <div style="font-size: 10px; line-height: 1.6;">
              <p>The Treasury-Indexed Private Obligations listed above are integrated with the Credit Enhancement 
              structure documented in this package. The BETH collateral specified in the Credit Enhancement Report 
              provides additional security backing for these obligations.</p>
              <p style="margin-top: 10px;"><strong>Cross-Reference Documents:</strong></p>
              <ul style="margin: 5px 0; padding-left: 20px;">
                <li>Credit Enhancement Report: ${instrumentNumbers.report}</li>
                <li>PAS Agreement: ${instrumentNumbers.pas}</li>
                <li>UCC-1 Filing: ${instrumentNumbers.ucc}</li>
              </ul>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; border: 1px solid #ddd; font-size: 9px;">
            <strong>Legal Disclaimer:</strong> This schedule is provided for informational purposes and forms part of the 
            complete Credit Enhancement Document Package. TIPO products are subject to the terms and conditions specified 
            in the individual TIPO agreements. Past performance does not guarantee future results. Treasury rate benchmarks 
            are publicly available reference rates and do not imply government backing.
          </div>
          
          <div class="footer">
            <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        ` : ''}
      </body>
      </html>
    `;
  };

  const generatePrintableDocument = (type, data) => {
    // Generate unique instrument number
    const generateInstrumentNumber = (prefix, id) => {
      const timestamp = Date.now().toString(36).toUpperCase();
      const random = Math.random().toString(36).substring(2, 6).toUpperCase();
      return `${prefix}-${timestamp}-${random}`;
    };
    
    // Watermark/seal SVG for all documents
    const watermarkSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.06; width: 400px; height: 400px; pointer-events: none;">
        <circle cx="100" cy="100" r="95" fill="none" stroke="#0a1628" stroke-width="2"/>
        <circle cx="100" cy="100" r="85" fill="none" stroke="#b8860b" stroke-width="1"/>
        <circle cx="100" cy="100" r="75" fill="none" stroke="#0a1628" stroke-width="1"/>
        <text x="100" y="85" text-anchor="middle" font-family="Georgia, serif" font-size="16" font-weight="bold" fill="#0a1628">BETHEREUM</text>
        <text x="100" y="105" text-anchor="middle" font-family="Georgia, serif" font-size="8" fill="#b8860b">PRIVATE WEALTH</text>
        <text x="100" y="118" text-anchor="middle" font-family="Georgia, serif" font-size="8" fill="#b8860b">MANAGEMENT</text>
        <path d="M 30 100 A 70 70 0 0 1 170 100" fill="none" stroke="#0a1628" stroke-width="1"/>
        <text x="100" y="145" text-anchor="middle" font-family="Georgia, serif" font-size="7" fill="#0a1628">ETHEREUM MAINNET</text>
        <polygon points="100,155 95,165 105,165" fill="#b8860b"/>
      </svg>
    `;
    
    // Security pattern background
    const securityPattern = `
      background-image: 
        repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(184, 134, 11, 0.02) 10px, rgba(184, 134, 11, 0.02) 20px),
        repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(10, 22, 40, 0.02) 10px, rgba(10, 22, 40, 0.02) 20px);
    `;
    
    const header = `
      <div style="position: relative; text-align: center; margin-bottom: 30px; border-bottom: 3px double #b8860b; padding-bottom: 20px;">
        <div style="position: absolute; top: 0; left: 0; width: 60px; height: 60px; border: 2px solid #0a1628; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
          <span style="font-size: 8px; color: #0a1628; text-align: center; line-height: 1.2;">OFFICIAL<br/>SEAL</span>
        </div>
        <h1 style="color: #0a1628; font-size: 32px; margin: 0; letter-spacing: 4px; font-family: Georgia, serif;">BETHEREUM</h1>
        <p style="color: #b8860b; font-size: 11px; letter-spacing: 3px; margin-top: 5px; font-weight: bold;">PRIVATE WEALTH MANAGEMENT</p>
        <p style="color: #666; font-size: 9px; letter-spacing: 2px; margin-top: 3px;">ETHEREUM MAINNET • ESTABLISHED 2024</p>
      </div>
    `;
    
    const footer = `
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #0a1628; font-size: 9px; color: #666; text-align: center;">
        <div style="display: flex; justify-content: center; gap: 30px; margin-bottom: 10px;">
          <span>🔒 SECURED DOCUMENT</span>
          <span>✓ BLOCKCHAIN VERIFIED</span>
          <span>⚡ ETHEREUM MAINNET</span>
        </div>
        <p style="margin: 5px 0;">This instrument is issued by Bethereum Private Wealth Management Platform</p>
        <p style="margin: 5px 0; font-family: monospace; font-size: 8px;">BETH Token: ${BETH_TOKEN}</p>
        <p style="margin: 5px 0;">Generated: ${new Date().toLocaleString()} | Document authenticity can be verified on-chain</p>
      </div>
    `;

    if (type === 'report') {
      const instrumentNo = data.report_id || data.guarantee_id || generateInstrumentNumber('CER', Date.now());
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Credit Enhancement Report - ${instrumentNo}</title>
          <style>
            body { font-family: 'Georgia', serif; padding: 40px; max-width: 850px; margin: 0 auto; position: relative; ${securityPattern} }
            .document-container { position: relative; padding: 30px; border: 3px double #0a1628; background: white; }
            .section { margin: 20px 0; padding: 15px; background: rgba(249, 249, 249, 0.9); border-left: 4px solid #b8860b; }
            .label { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 1px; }
            .value { font-size: 16px; color: #0a1628; margin-top: 5px; font-weight: 500; }
            .signature-box { border: 2px solid #0a1628; padding: 25px; margin: 20px 0; text-align: center; background: white; }
            .instrument-number { position: absolute; top: 10px; right: 10px; font-family: 'Courier New', monospace; font-size: 10px; color: #0a1628; background: #f0f0f0; padding: 5px 10px; border: 1px solid #ccc; }
            .official-stamp { position: absolute; bottom: 120px; right: 30px; width: 100px; height: 100px; border: 2px solid #b8860b; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0.7; transform: rotate(-15deg); }
            .official-stamp span { font-size: 8px; text-align: center; color: #b8860b; font-weight: bold; }
            @media print { body { padding: 20px; } .document-container { border: 2px solid #000; } }
          </style>
        </head>
        <body>
          <div class="document-container">
            ${watermarkSVG}
            <div class="instrument-number">INSTRUMENT NO: ${instrumentNo}</div>
            ${header}
            <h2 style="text-align: center; color: #0a1628; font-size: 22px; border: 2px solid #0a1628; padding: 15px; margin: 20px 0; letter-spacing: 2px;">CREDIT ENHANCEMENT REPORT</h2>
            <p style="text-align: center; color: #666; font-size: 12px;">This document certifies the credit enhancement arrangement between the parties listed below</p>
            
            <div class="section">
              <div class="label">DEBTOR / BORROWER INFORMATION</div>
              <div class="value">${data.borrower_name}</div>
              <div style="font-family: 'Courier New', monospace; font-size: 11px; color: #666; margin-top: 8px; background: #f5f5f5; padding: 8px; border-radius: 3px;">
                Wallet Address: ${data.borrower_wallet}
              </div>
            </div>
            
            <div class="section">
              <div class="label">SECURED PARTY / LENDER</div>
              <div class="value">${data.lender_name}</div>
            </div>
            
            <div class="section" style="background: linear-gradient(135deg, #fefefe 0%, #f8f4e8 100%); border-left: 6px solid #b8860b;">
              <div class="label">BETH COLLATERAL COMMITTED</div>
              <div class="value" style="font-size: 28px; color: #b8860b; font-weight: bold;">${(data.internal_beth_collateral || data.beth_amount || 0).toLocaleString()} BETH</div>
              <div style="color: #0a1628; font-size: 14px; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ccc;">
                USD Equivalent Value: <strong>$${(data.conditional_guarantee_usd || data.beth_amount || 0).toLocaleString()}</strong>
              </div>
            </div>
            
            <div style="background: #f9f9f9; padding: 15px; margin: 20px 0; border: 1px solid #ddd; font-size: 11px; line-height: 1.6;">
              <strong>CONDITIONS & TERMS:</strong>
              <ol style="margin: 10px 0 0 0; padding-left: 20px;">
                <li>This report is for informational purposes and does not constitute debt settlement.</li>
                <li>Any transaction is conditional upon lender acceptance.</li>
                <li>All BETH backing remains internal to BETH treasury for audit purposes.</li>
                <li>No value transmitted unless lender explicitly accepts the settlement.</li>
              </ol>
            </div>
            
            <div style="display: flex; gap: 20px; margin-top: 40px;">
              <div class="signature-box" style="flex: 1;">
                <div class="label">DEBTOR SIGNATURE</div>
                <div style="margin-top: 50px; border-top: 2px solid #0a1628; width: 200px; margin-left: auto; margin-right: auto;"></div>
                <div style="font-size: 11px; margin-top: 8px;">Name: ${data.borrower_name}</div>
                <div style="font-size: 11px; margin-top: 5px;">Date: _________________</div>
              </div>
              <div class="signature-box" style="flex: 1;">
                <div class="label">SECURED PARTY SIGNATURE</div>
                <div style="margin-top: 50px; border-top: 2px solid #0a1628; width: 200px; margin-left: auto; margin-right: auto;"></div>
                <div style="font-size: 11px; margin-top: 8px;">Name: ${data.lender_name}</div>
                <div style="font-size: 11px; margin-top: 5px;">Date: _________________</div>
              </div>
            </div>
            
            <div class="official-stamp">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>BETHEREUM</span>
              <span>SEAL</span>
            </div>
            
            ${footer}
          </div>
        </body>
        </html>
      `;
    }
    
    if (type === 'pas') {
      const instrumentNo = data.agreement_id || data.guarantee_id || generateInstrumentNumber('PAS', Date.now());
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>PAS Agreement - ${instrumentNo}</title>
          <style>
            body { font-family: 'Georgia', serif; padding: 40px; max-width: 850px; margin: 0 auto; position: relative; ${securityPattern} }
            .document-container { position: relative; padding: 30px; border: 3px double #0a1628; background: white; }
            .section { margin: 20px 0; padding: 15px; background: rgba(249, 249, 249, 0.9); border-left: 4px solid #b8860b; }
            .label { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 1px; }
            .value { font-size: 16px; color: #0a1628; margin-top: 5px; font-weight: 500; }
            .signature-box { border: 2px solid #0a1628; padding: 25px; margin: 20px 0; text-align: center; background: white; }
            .instrument-number { position: absolute; top: 10px; right: 10px; font-family: 'Courier New', monospace; font-size: 10px; color: #0a1628; background: #f0f0f0; padding: 5px 10px; border: 1px solid #ccc; }
            .official-stamp { position: absolute; bottom: 120px; right: 30px; width: 100px; height: 100px; border: 2px solid #b8860b; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0.7; transform: rotate(-15deg); }
            .official-stamp span { font-size: 8px; text-align: center; color: #b8860b; font-weight: bold; }
            .legal-text { font-size: 11px; line-height: 1.8; text-align: justify; }
            @media print { body { padding: 20px; } .document-container { border: 2px solid #000; } }
          </style>
        </head>
        <body>
          <div class="document-container">
            ${watermarkSVG}
            <div class="instrument-number">INSTRUMENT NO: ${instrumentNo}</div>
            ${header}
            <h2 style="text-align: center; color: #0a1628; font-size: 20px; border: 2px solid #0a1628; padding: 15px; margin: 20px 0; letter-spacing: 2px;">PRIVATE ACCORD & SATISFACTION AGREEMENT</h2>
            <p style="text-align: center; color: #666; font-size: 12px;">Legally Binding Settlement Instrument Under UCC Article 3</p>
            
            <div class="section">
              <div class="label">FIRST PARTY (DEBTOR)</div>
              <div class="value">${data.borrower_name}</div>
              <div style="font-family: 'Courier New', monospace; font-size: 11px; color: #666; margin-top: 8px; background: #f5f5f5; padding: 8px; border-radius: 3px;">
                Wallet: ${data.borrower_wallet}
              </div>
            </div>
            
            <div class="section">
              <div class="label">SECOND PARTY (SECURED PARTY/LENDER)</div>
              <div class="value">${data.lender_name}</div>
              ${data.lender_contact ? `<div style="font-size: 12px; color: #666; margin-top: 5px;">Contact: ${data.lender_contact}</div>` : ''}
            </div>
            
            <div class="section" style="background: linear-gradient(135deg, #fefefe 0%, #f8f4e8 100%); border-left: 6px solid #b8860b;">
              <div class="label">SETTLEMENT AMOUNT</div>
              <div class="value" style="font-size: 28px; color: #b8860b; font-weight: bold;">${(data.beth_collateral || data.beth_amount || 0).toLocaleString()} BETH</div>
              <div style="color: #0a1628; font-size: 14px; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ccc;">
                In full settlement and satisfaction of all claims
              </div>
            </div>
            
            <div style="background: white; padding: 20px; margin: 20px 0; border: 1px solid #0a1628;">
              <p class="legal-text">
                <strong>WHEREAS,</strong> the First Party (Debtor) and Second Party (Secured Party) desire to settle and resolve any and all claims, disputes, or obligations between them;
              </p>
              <p class="legal-text">
                <strong>NOW, THEREFORE,</strong> in consideration of the mutual covenants herein, the parties agree that upon acceptance and execution of this Agreement, the settlement amount specified above shall constitute full accord and satisfaction of any debts, claims, or obligations.
              </p>
              <p class="legal-text">
                <strong>GOVERNING LAW:</strong> ${data.governing_law || 'State of Delaware, USA'}
              </p>
            </div>
            
            <div style="display: flex; gap: 20px; margin-top: 40px;">
              <div class="signature-box" style="flex: 1;">
                <div class="label">FIRST PARTY ACCEPTANCE</div>
                <div style="margin-top: 50px; border-top: 2px solid #0a1628; width: 200px; margin-left: auto; margin-right: auto;"></div>
                <div style="font-size: 11px; margin-top: 8px;">Name: ${data.borrower_name}</div>
                <div style="font-size: 11px; margin-top: 5px;">Date: _________________</div>
              </div>
              <div class="signature-box" style="flex: 1;">
                <div class="label">SECOND PARTY ACCEPTANCE</div>
                <div style="margin-top: 50px; border-top: 2px solid #0a1628; width: 200px; margin-left: auto; margin-right: auto;"></div>
                <div style="font-size: 11px; margin-top: 8px;">Name: ${data.lender_name}</div>
                <div style="font-size: 11px; margin-top: 5px;">Date: _________________</div>
              </div>
            </div>
            
            <div class="official-stamp">
              <span>OFFICIAL</span>
              <span style="font-size: 10px;">✦</span>
              <span>BETHEREUM</span>
              <span>SEAL</span>
            </div>
            
            ${footer}
          </div>
        </body>
        </html>
      `;
    }
    
    if (type === 'check') {
      const instrumentNo = `MO-${data.check_number || Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      const routingNumber = '021000021'; // Symbolic routing number
      const accountNumber = `BETH${data.check_id?.slice(-8) || '00000000'}`.toUpperCase();
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Tokenized Check - ${data.check_number}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=MICR+Encoding&display=swap');
            body { 
              font-family: 'Georgia', serif; 
              padding: 40px; 
              background: #f5f5f5;
            }
            .money-order { 
              width: 750px;
              height: 320px;
              margin: 0 auto;
              background: linear-gradient(135deg, #fefefe 0%, #f8f8f0 25%, #fefefe 50%, #f0f8f0 75%, #fefefe 100%);
              border: 3px solid #0a1628;
              position: relative;
              padding: 0;
              box-shadow: 0 4px 20px rgba(0,0,0,0.15);
              overflow: hidden;
            }
            .security-pattern {
              position: absolute;
              top: 0; left: 0; right: 0; bottom: 0;
              background-image: 
                repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(184, 134, 11, 0.03) 2px, rgba(184, 134, 11, 0.03) 4px),
                repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(10, 22, 40, 0.03) 2px, rgba(10, 22, 40, 0.03) 4px);
              pointer-events: none;
            }
            .border-design {
              position: absolute;
              top: 8px; left: 8px; right: 8px; bottom: 8px;
              border: 2px solid #b8860b;
              pointer-events: none;
            }
            .inner-border {
              position: absolute;
              top: 12px; left: 12px; right: 12px; bottom: 12px;
              border: 1px dashed rgba(184, 134, 11, 0.5);
              pointer-events: none;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-size: 80px;
              font-weight: bold;
              color: rgba(10, 22, 40, 0.04);
              letter-spacing: 15px;
              pointer-events: none;
              white-space: nowrap;
            }
            .header-section {
              display: flex;
              justify-content: space-between;
              padding: 15px 25px 10px 25px;
              border-bottom: 2px solid #0a1628;
              background: linear-gradient(180deg, rgba(10, 22, 40, 0.05) 0%, transparent 100%);
              position: relative;
              z-index: 1;
            }
            .logo-section {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            .logo-seal {
              width: 55px;
              height: 55px;
              border: 2px solid #0a1628;
              border-radius: 50%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: white;
            }
            .logo-seal span {
              font-size: 7px;
              font-weight: bold;
              color: #0a1628;
              line-height: 1.1;
            }
            .company-name h1 {
              margin: 0;
              font-size: 22px;
              color: #0a1628;
              letter-spacing: 3px;
            }
            .company-name p {
              margin: 2px 0 0 0;
              font-size: 9px;
              color: #b8860b;
              letter-spacing: 2px;
            }
            .instrument-info {
              text-align: right;
              font-size: 10px;
            }
            .instrument-info .number {
              font-family: 'Courier New', monospace;
              font-size: 14px;
              font-weight: bold;
              color: #0a1628;
              background: #f0f0f0;
              padding: 3px 8px;
              border: 1px solid #ccc;
            }
            .instrument-info .date {
              margin-top: 5px;
              color: #666;
            }
            .body-section {
              padding: 15px 25px;
              position: relative;
              z-index: 1;
            }
            .payee-line {
              margin-bottom: 12px;
            }
            .payee-label {
              font-size: 9px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .payee-value {
              font-size: 18px;
              font-weight: bold;
              color: #0a1628;
              border-bottom: 2px solid #0a1628;
              padding: 5px 0;
              margin-top: 3px;
            }
            .payee-wallet {
              font-family: 'Courier New', monospace;
              font-size: 9px;
              color: #888;
              margin-top: 3px;
            }
            .amount-section {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin: 15px 0;
            }
            .amount-words {
              flex: 1;
              font-size: 12px;
              border-bottom: 1px solid #999;
              padding: 5px 0;
              color: #333;
            }
            .amount-box {
              margin-left: 20px;
              border: 3px solid #0a1628;
              padding: 8px 15px;
              background: linear-gradient(180deg, #fff 0%, #f8f8f0 100%);
              min-width: 140px;
              text-align: center;
            }
            .amount-box .currency {
              font-size: 10px;
              color: #666;
            }
            .amount-box .value {
              font-size: 22px;
              font-weight: bold;
              color: #0a1628;
            }
            .memo-section {
              font-size: 10px;
              color: #666;
              padding: 8px;
              background: rgba(184, 134, 11, 0.1);
              border-left: 3px solid #b8860b;
              margin: 10px 0;
            }
            .footer-section {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              padding: 10px 25px 15px 25px;
              position: relative;
              z-index: 1;
            }
            .micr-line {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              letter-spacing: 3px;
              color: #333;
            }
            .signature-area {
              text-align: right;
            }
            .signature-line {
              border-top: 2px solid #0a1628;
              width: 200px;
              margin-bottom: 5px;
            }
            .signature-label {
              font-size: 9px;
              color: #666;
            }
            .status-badge {
              position: absolute;
              top: 15px;
              right: 160px;
              padding: 3px 10px;
              font-size: 9px;
              font-weight: bold;
              letter-spacing: 1px;
              border-radius: 3px;
              z-index: 2;
            }
            .status-draft { background: #fef3c7; color: #92400e; border: 1px solid #f59e0b; }
            .status-issued { background: #d1fae5; color: #065f46; border: 1px solid #10b981; }
            .void-overlay {
              display: ${data.status === 'void' ? 'block' : 'none'};
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 60px;
              font-weight: bold;
              color: rgba(220, 38, 38, 0.3);
              letter-spacing: 20px;
              z-index: 10;
            }
            @media print { 
              body { padding: 10px; background: white; }
              .money-order { box-shadow: none; border: 2px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="money-order">
            <div class="security-pattern"></div>
            <div class="border-design"></div>
            <div class="inner-border"></div>
            <div class="watermark">BETHEREUM</div>
            <div class="void-overlay">VOID</div>
            
            <div class="status-badge ${data.status === 'issued' ? 'status-issued' : 'status-draft'}">
              ${(data.status || 'DRAFT').toUpperCase()}
            </div>
            
            <div class="header-section">
              <div class="logo-section">
                <div class="logo-seal">
                  <span>OFFICIAL</span>
                  <span style="font-size: 10px;">✦</span>
                  <span>SEAL</span>
                </div>
                <div class="company-name">
                  <h1>BETHEREUM</h1>
                  <p>TOKENIZED CHECK</p>
                </div>
              </div>
              <div class="instrument-info">
                <div class="number">No. ${data.check_number}</div>
                <div class="date">Date: ${new Date(data.created_at || Date.now()).toLocaleDateString()}</div>
                <div style="margin-top: 3px; font-size: 8px; color: #999;">Instrument: ${instrumentNo}</div>
              </div>
            </div>
            
            <div class="body-section">
              <div class="payee-line">
                <div class="payee-label">Pay to the Order of</div>
                <div class="payee-value">${data.payee_name}</div>
                <div class="payee-wallet">${data.payee_wallet}</div>
              </div>
              
              <div class="amount-section">
                <div class="amount-words">
                  <strong>${numberToWords(data.amount)}</strong> BETH ********
                </div>
                <div class="amount-box">
                  <div class="currency">BETH</div>
                  <div class="value">${data.amount?.toLocaleString()}</div>
                </div>
              </div>
              
              ${data.memo ? `
                <div class="memo-section">
                  <strong>MEMO:</strong> ${data.memo}
                </div>
              ` : ''}
            </div>
            
            <div class="footer-section">
              <div class="micr-line">
                ⑆${routingNumber}⑆ ${accountNumber}⑈ ${String(data.check_number).padStart(6, '0')}
              </div>
              <div class="signature-area">
                <div class="signature-line"></div>
                <div class="signature-label">Authorized Signature</div>
              </div>
            </div>
          </div>
          
          <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #666;">
            <p>This tokenized check is backed by BETH tokens on Ethereum Mainnet</p>
            <p style="font-family: monospace; font-size: 9px;">BETH Contract: ${BETH_TOKEN}</p>
            ${data.tx_hash ? `<p style="font-family: monospace; font-size: 9px;">TX Hash: ${data.tx_hash}</p>` : ''}
          </div>
        </body>
        </html>
      `;
    }
    
    if (type === 'ucc') {
      // Use the official Tennessee Secretary of State UCC-1 format (IACA 2023)
      return generateOfficialUCC1({
        filing_number: data.filing_number || generateInstrumentNumber('UCC', Date.now()),
        debtor_name: data.debtor_name,
        debtor_org_name: data.debtor_org_name || '',
        debtor_surname: data.debtor_name?.split(' ').pop() || '',
        debtor_first_name: data.debtor_name?.split(' ')[0] || '',
        debtor_address: data.debtor_address,
        debtor_city: data.debtor_city || '',
        debtor_state: data.debtor_state || 'TN',
        debtor_zip: data.debtor_zip || '',
        debtor_tax_id: data.debtor_tax_id || '',
        debtor_org_type: data.debtor_org_type || '',
        debtor_jurisdiction: 'Tennessee',
        secured_party_name: data.secured_party_name || 'Bethereum Private Wealth Management',
        secured_party_address: data.secured_party_address || '',
        secured_party_city: data.secured_party_city || '',
        secured_party_state: data.secured_party_state || 'TN',
        secured_party_zip: data.secured_party_zip || '',
        collateral_description: `All BETH tokens (ERC-20) now owned or hereafter acquired by Debtor, contract address ${BETH_TOKEN} on Ethereum Mainnet; all rights to receive, transfer, exchange, or otherwise use or dispose of such tokens; all rights under the Credit Enhancement Agreement and Performance Assurance Structure (PAS) dated ${data.agreement_date || new Date().toISOString().split('T')[0]}; all tokenized checks or payment instruments issued thereunder; all accounts, general intangibles, and payment intangibles arising therefrom; and any substitutions, replacements, accessions, or proceeds thereof.`,
        max_indebtedness: data.max_indebtedness || '0',
        beth_collateral: data.beth_collateral || '0',
        agreement_date: data.agreement_date || new Date().toISOString().split('T')[0],
        filing_status: 'PENDING SUBMISSION',
        filer_reference: data.filing_number || generateInstrumentNumber('UCC', Date.now()),
        submitter_email: user?.email || 'support@bethereum.io'
      });
    }
    
    return '<html><body>Document not found</body></html>';
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    if (num >= 1000000) return `${numberToWords(Math.floor(num / 1000000))} Million ${numberToWords(num % 1000000)}`.trim();
    if (num >= 1000) return `${numberToWords(Math.floor(num / 1000))} Thousand ${numberToWords(num % 1000)}`.trim();
    if (num >= 100) return `${ones[Math.floor(num / 100)]} Hundred ${numberToWords(num % 100)}`.trim();
    if (num >= 20) return `${tens[Math.floor(num / 10)]} ${ones[num % 10]}`.trim();
    if (num >= 10) return teens[num - 10];
    return ones[num];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500/20 text-gray-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'issued': case 'filed': return 'bg-blue-500/20 text-blue-400';
      case 'redeemed': case 'fulfilled': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="credit-enhancement-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">← DASHBOARD</Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide">Credit Enhancement & PAS Management</h1>
              <p className="text-xs text-gray-500">Create reports, agreements, tokenized checks & UCC filings</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {account ? (
              <div className="text-right">
                <p className="text-xs text-gray-500">Connected</p>
                <p className="text-sm text-white font-mono">{account.slice(0, 8)}...{account.slice(-6)}</p>
              </div>
            ) : (
              <Button onClick={connectWallet} size="sm" className="bg-[#b8860b] hover:bg-[#9a7209]">Connect Wallet</Button>
            )}
            <Button variant="ghost" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardContent className="py-4">
              <p className="text-xs text-gray-500 tracking-wider">TOTAL BETH</p>
              <p className="text-2xl font-light text-white">{(treasurySummary?.total_beth_committed || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardContent className="py-4">
              <p className="text-xs text-gray-500 tracking-wider">GUARANTEES</p>
              <p className="text-2xl font-light text-white">{treasurySummary?.active_guarantees || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardContent className="py-4">
              <p className="text-xs text-gray-500 tracking-wider">CHECKS</p>
              <p className="text-2xl font-light text-white">{tokenizedChecks.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardContent className="py-4">
              <p className="text-xs text-gray-500 tracking-wider">UCC FILINGS</p>
              <p className="text-2xl font-light text-white">{uccFilings.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardContent className="py-4">
              <p className="text-xs text-gray-500 tracking-wider">FULFILLED</p>
              <p className="text-2xl font-light text-green-400">{treasurySummary?.fulfilled_beth || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 border-b border-[#1a2d4a] overflow-x-auto">
          {[
            { id: 'reports', label: 'CREDIT ENHANCEMENT', icon: FileText },
            { id: 'pas', label: 'PAS AGREEMENTS', icon: Shield },
            { id: 'checks', label: 'TOKENIZED CHECKS', icon: CreditCard },
            { id: 'ucc', label: 'UCC-1 FILINGS', icon: Scale },
            { id: 'package', label: 'DOCUMENT PACKAGE', icon: Package },
            { id: 'history', label: 'ALL DOCUMENTS', icon: FileCheck }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-medium tracking-wider transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'text-[#b8860b] border-b-2 border-[#b8860b]' : 'text-gray-500 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Credit Enhancement Tab */}
        {activeTab === 'reports' && (
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardHeader>
              <CardTitle className="text-[#b8860b] text-sm tracking-wider flex items-center gap-2">
                <FileText className="w-5 h-5" /> CREATE CREDIT ENHANCEMENT REPORT
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createReport} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">DEBTOR/BORROWER NAME *</label>
                  <Input value={reportForm.borrower_name} onChange={(e) => setReportForm({ ...reportForm, borrower_name: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">DEBTOR WALLET *</label>
                  <Input value={reportForm.borrower_wallet} onChange={(e) => setReportForm({ ...reportForm, borrower_wallet: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white font-mono" placeholder="0x..." required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">SECURED PARTY/LENDER NAME *</label>
                  <Input value={reportForm.lender_name} onChange={(e) => setReportForm({ ...reportForm, lender_name: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">BETH COLLATERAL *</label>
                  <Input type="number" value={reportForm.beth_collateral} onChange={(e) => setReportForm({ ...reportForm, beth_collateral: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div className="md:col-span-2 flex gap-4">
                  <Button type="submit" disabled={loading} className="bg-[#b8860b] hover:bg-[#9a7209]">
                    {loading ? 'CREATING...' : 'CREATE REPORT'}
                  </Button>
                  <p className="text-xs text-gray-500 self-center">After creation, UCC-1 form will be pre-filled automatically</p>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* PAS Tab */}
        {activeTab === 'pas' && (
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardHeader>
              <CardTitle className="text-[#b8860b] text-sm tracking-wider flex items-center gap-2">
                <Shield className="w-5 h-5" /> CREATE PAS AGREEMENT
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createPAS} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">DEBTOR NAME *</label>
                  <Input value={pasForm.borrower_name} onChange={(e) => setPasForm({ ...pasForm, borrower_name: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">DEBTOR WALLET *</label>
                  <Input value={pasForm.borrower_wallet} onChange={(e) => setPasForm({ ...pasForm, borrower_wallet: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white font-mono" placeholder="0x..." required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">SECURED PARTY NAME *</label>
                  <Input value={pasForm.lender_name} onChange={(e) => setPasForm({ ...pasForm, lender_name: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 tracking-wider mb-2">BETH COLLATERAL *</label>
                  <Input type="number" value={pasForm.beth_collateral} onChange={(e) => setPasForm({ ...pasForm, beth_collateral: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                </div>
                <div className="md:col-span-2 flex gap-4">
                  <Button type="submit" disabled={loading} className="bg-[#b8860b] hover:bg-[#9a7209]">
                    {loading ? 'CREATING...' : 'CREATE PAS AGREEMENT'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tokenized Checks Tab */}
        {activeTab === 'checks' && (
          <div className="space-y-6">
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardHeader>
                <CardTitle className="text-[#b8860b] text-sm tracking-wider flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> CREATE TOKENIZED CHECK
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createTokenizedCheck} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-gray-500 tracking-wider mb-2">PAYEE NAME *</label>
                    <Input value={checkForm.payee_name} onChange={(e) => setCheckForm({ ...checkForm, payee_name: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 tracking-wider mb-2">PAYEE WALLET *</label>
                    <Input value={checkForm.payee_wallet} onChange={(e) => setCheckForm({ ...checkForm, payee_wallet: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white font-mono" placeholder="0x..." required />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 tracking-wider mb-2">AMOUNT (BETH) *</label>
                    <Input type="number" value={checkForm.amount} onChange={(e) => setCheckForm({ ...checkForm, amount: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" required />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 tracking-wider mb-2">MEMO</label>
                    <Input value={checkForm.memo} onChange={(e) => setCheckForm({ ...checkForm, memo: e.target.value })} className="bg-[#0a1628] border-[#2a3d5a] text-white" />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" disabled={loading} className="bg-[#b8860b] hover:bg-[#9a7209]">
                      {loading ? 'CREATING...' : 'CREATE TOKENIZED CHECK'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardHeader><CardTitle className="text-white text-sm">ISSUED CHECKS</CardTitle></CardHeader>
              <CardContent>
                {tokenizedChecks.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No tokenized checks yet</p>
                ) : (
                  <div className="space-y-4">
                    {tokenizedChecks.map(check => (
                      <div key={check.check_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#2a3d5a]">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-[#b8860b]" />
                            <div>
                              <p className="text-white font-medium">Check #{check.check_number}</p>
                              <p className="text-xs text-gray-500">{check.payee_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-[#b8860b]">{check.amount?.toLocaleString()} BETH</p>
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(check.status)}`}>{check.status?.toUpperCase()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {check.status === 'draft' && (
                            <Button size="sm" onClick={() => issueCheck(check.check_id)} disabled={loading || !account} className="bg-green-600 hover:bg-green-700">
                              <Wallet className="w-4 h-4 mr-1" /> Issue
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => printDocument('check', check)}>
                            <Printer className="w-4 h-4 mr-1" /> Print
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* UCC-1 Tab */}
        {activeTab === 'ucc' && (
          <div className="space-y-6">
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardHeader>
                <CardTitle className="text-[#b8860b] text-sm tracking-wider flex items-center gap-2">
                  <Scale className="w-5 h-5" /> CREATE UCC-1 FINANCING STATEMENT (TN SEC. OF STATE)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createUCCFiling} className="space-y-6">
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#2a3d5a]">
                    <p className="text-[#b8860b] text-xs tracking-wider mb-4">1. DEBTOR INFORMATION</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-2">DEBTOR NAME (Organization or Individual) *</label>
                        <Input value={uccForm.debtor_name} onChange={(e) => setUccForm({ ...uccForm, debtor_name: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" required />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-2">MAILING ADDRESS</label>
                        <Input value={uccForm.debtor_address} onChange={(e) => setUccForm({ ...uccForm, debtor_address: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" placeholder="Street Address" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">CITY</label>
                        <Input value={uccForm.debtor_city} onChange={(e) => setUccForm({ ...uccForm, debtor_city: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">STATE</label>
                          <Input value={uccForm.debtor_state} onChange={(e) => setUccForm({ ...uccForm, debtor_state: e.target.value.toUpperCase() })} maxLength={2} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">ZIP</label>
                          <Input value={uccForm.debtor_zip} onChange={(e) => setUccForm({ ...uccForm, debtor_zip: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-2">ETHEREUM WALLET ADDRESS</label>
                        <Input value={uccForm.debtor_wallet} onChange={(e) => setUccForm({ ...uccForm, debtor_wallet: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono" placeholder="0x..." />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#2a3d5a]">
                    <p className="text-[#b8860b] text-xs tracking-wider mb-4">3. SECURED PARTY INFORMATION</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-2">SECURED PARTY NAME *</label>
                        <Input value={uccForm.secured_party_name} onChange={(e) => setUccForm({ ...uccForm, secured_party_name: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" required />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-2">MAILING ADDRESS</label>
                        <Input value={uccForm.secured_party_address} onChange={(e) => setUccForm({ ...uccForm, secured_party_address: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">CITY</label>
                        <Input value={uccForm.secured_party_city} onChange={(e) => setUccForm({ ...uccForm, secured_party_city: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">STATE</label>
                          <Input value={uccForm.secured_party_state} onChange={(e) => setUccForm({ ...uccForm, secured_party_state: e.target.value.toUpperCase() })} maxLength={2} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">ZIP</label>
                          <Input value={uccForm.secured_party_zip} onChange={(e) => setUccForm({ ...uccForm, secured_party_zip: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#2a3d5a]">
                    <p className="text-[#b8860b] text-xs tracking-wider mb-4">4. COLLATERAL & FILING</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">FILING JURISDICTION</label>
                        <select value={uccForm.filing_state} onChange={(e) => setUccForm({ ...uccForm, filing_state: e.target.value })} className="w-full p-2 bg-[#1a2d4a] border border-[#2a3d5a] rounded text-white">
                          <option value="Tennessee">Tennessee (TN)</option>
                          <option value="Delaware">Delaware</option>
                          <option value="Nevada">Nevada</option>
                          <option value="Wyoming">Wyoming</option>
                          <option value="Texas">Texas</option>
                          <option value="California">California</option>
                          <option value="New York">New York</option>
                          <option value="Florida">Florida</option>
                          <option value="Georgia">Georgia</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">AGREEMENT DATE</label>
                        <Input type="date" value={uccForm.agreement_date} onChange={(e) => setUccForm({ ...uccForm, agreement_date: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">BETH COLLATERAL AMOUNT *</label>
                        <Input type="number" value={uccForm.beth_collateral} onChange={(e) => setUccForm({ ...uccForm, beth_collateral: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" required />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-2">MAX INDEBTEDNESS (TAX)</label>
                        <Input type="number" step="0.01" value={uccForm.max_indebtedness} onChange={(e) => setUccForm({ ...uccForm, max_indebtedness: e.target.value })} className="bg-[#1a2d4a] border-[#2a3d5a] text-white" placeholder="0.00" />
                      </div>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading} className="bg-[#b8860b] hover:bg-[#9a7209]">
                    {loading ? 'CREATING...' : 'CREATE UCC-1 FILING'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardHeader><CardTitle className="text-white text-sm">UCC-1 FILINGS</CardTitle></CardHeader>
              <CardContent>
                {uccFilings.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No UCC filings yet</p>
                ) : (
                  <div className="space-y-4">
                    {uccFilings.map(filing => (
                      <div key={filing.filing_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#2a3d5a]">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">{filing.filing_number}</p>
                            <p className="text-xs text-gray-500">{filing.debtor_name} → {filing.secured_party_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[#b8860b] font-bold">{parseFloat(filing.beth_collateral || 0).toLocaleString()} BETH</p>
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(filing.status)}`}>{filing.status?.toUpperCase()}</span>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => printDocument('ucc', filing)}>
                          <Printer className="w-4 h-4 mr-1" /> Print UCC-1
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Document Package Tab */}
        {activeTab === 'package' && (
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]" data-testid="document-package-tab">
            <CardHeader>
              <CardTitle className="text-[#b8860b] text-sm tracking-wider flex items-center gap-2">
                <Package className="w-5 h-5" /> GENERATE COMPLETE DOCUMENT PACKAGE
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-[#0a1628] rounded-lg border border-[#b8860b]/30">
                <div className="flex items-start gap-3">
                  <Layers className="w-6 h-6 text-[#b8860b] mt-1" />
                  <div>
                    <h3 className="text-white font-medium mb-2">Credit Enhancement Document Bundle</h3>
                    <p className="text-gray-400 text-sm">
                      Generate a complete multi-page document package containing all Credit Enhancement documents 
                      with consistent watermarks, instrument numbers, and professional formatting.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs px-2 py-1 bg-[#1a2d4a] text-[#b8860b] rounded border border-[#b8860b]/30">
                        <FileText className="w-3 h-3 inline mr-1" />Credit Enhancement Report
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#1a2d4a] text-[#b8860b] rounded border border-[#b8860b]/30">
                        <Shield className="w-3 h-3 inline mr-1" />PAS Agreement
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#1a2d4a] text-[#b8860b] rounded border border-[#b8860b]/30">
                        <Scale className="w-3 h-3 inline mr-1" />UCC-1 Filing
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#1a2d4a] text-[#b8860b] rounded border border-[#b8860b]/30">
                        <CreditCard className="w-3 h-3 inline mr-1" />Tokenized Check
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#1a2d4a] text-[#b8860b] rounded border border-[#b8860b]/30">
                        <FileCheck className="w-3 h-3 inline mr-1" />Check Disclosure Agreement
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                generateDocumentPackage({
                  borrower_name: formData.get('pkg_borrower_name'),
                  borrower_wallet: formData.get('pkg_borrower_wallet'),
                  borrower_address: formData.get('pkg_borrower_address'),
                  lender_name: formData.get('pkg_lender_name'),
                  lender_wallet: formData.get('pkg_lender_wallet'),
                  lender_address: formData.get('pkg_lender_address'),
                  beth_collateral: formData.get('pkg_beth_collateral')
                });
              }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4 p-4 bg-[#0a1628] rounded-lg">
                    <h4 className="text-[#b8860b] text-xs tracking-wider flex items-center gap-2">
                      <FileText className="w-4 h-4" />DEBTOR / BORROWER INFORMATION
                    </h4>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">NAME *</label>
                      <Input 
                        name="pkg_borrower_name"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white" 
                        placeholder="Full legal name"
                        required 
                        data-testid="pkg-borrower-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">WALLET ADDRESS *</label>
                      <Input 
                        name="pkg_borrower_wallet"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono text-sm" 
                        placeholder="0x..."
                        required 
                        data-testid="pkg-borrower-wallet"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">MAILING ADDRESS</label>
                      <Input 
                        name="pkg_borrower_address"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white" 
                        placeholder="Street, City, State, ZIP"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4 p-4 bg-[#0a1628] rounded-lg">
                    <h4 className="text-[#b8860b] text-xs tracking-wider flex items-center gap-2">
                      <Shield className="w-4 h-4" />SECURED PARTY / LENDER INFORMATION
                    </h4>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">NAME *</label>
                      <Input 
                        name="pkg_lender_name"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white" 
                        placeholder="Full legal name"
                        required 
                        data-testid="pkg-lender-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">WALLET ADDRESS</label>
                      <Input 
                        name="pkg_lender_wallet"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono text-sm" 
                        placeholder="0x..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">MAILING ADDRESS</label>
                      <Input 
                        name="pkg_lender_address"
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white" 
                        placeholder="Street, City, State, ZIP"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-[#b8860b]/30">
                  <h4 className="text-[#b8860b] text-xs tracking-wider mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />COLLATERAL AMOUNT
                  </h4>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 tracking-wider mb-2">BETH COLLATERAL *</label>
                      <Input 
                        name="pkg_beth_collateral"
                        type="number"
                        className="bg-[#0a1628] border-[#b8860b] text-white text-xl" 
                        placeholder="Amount of BETH"
                        required 
                        data-testid="pkg-beth-collateral"
                      />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Current BETH Balance</p>
                      <p className="text-[#b8860b] font-bold">{parseFloat(bethBalance || 0).toLocaleString()} BETH</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between items-center pt-4 border-t border-[#2a3d5a]">
                  <div className="text-sm text-gray-400">
                    <p>This will generate a 6-page PDF-ready document package</p>
                    <p className="text-xs text-gray-500 mt-1">Cover page + Report + PAS + UCC-1 + Tokenized Check + Check Disclosure + TIPO (if applicable)</p>
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-[#b8860b] hover:bg-[#9a7209] text-white px-8"
                    disabled={loading}
                    data-testid="generate-package-btn"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Document Package (PDF)
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Documents History Tab */}
        {activeTab === 'history' && (
          <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
            <CardHeader><CardTitle className="text-white text-sm">ALL DOCUMENTS</CardTitle></CardHeader>
            <CardContent>
              {guarantees.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No documents created yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#2a3d5a]">
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">ID</th>
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">TYPE</th>
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">DEBTOR</th>
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">AMOUNT</th>
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">STATUS</th>
                        <th className="text-left py-3 px-4 text-xs text-[#b8860b]">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guarantees.map(g => (
                        <tr key={g.guarantee_id} className="border-b border-[#1a2d4a]">
                          <td className="py-3 px-4 text-sm font-mono text-[#b8860b]">{g.guarantee_id}</td>
                          <td className="py-3 px-4 text-sm text-gray-400">
                            {g.guarantee_id?.startsWith('CER') ? 'Report' : g.guarantee_id?.startsWith('PAS') ? 'PAS' : 'Guarantee'}
                          </td>
                          <td className="py-3 px-4 text-sm text-white">{g.borrower_name}</td>
                          <td className="py-3 px-4 text-sm text-white">{g.beth_amount?.toLocaleString()} BETH</td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(g.status)}`}>{g.status?.toUpperCase()}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => printDocument('report', g)}><Printer className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => prefillUccFromGuarantee(g)} title="Create UCC-1"><Scale className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => openEsignModal('credit_enhancement', g)} title="Request E-Signature"><Mail className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* E-Signature Request Modal */}
        {showEsignModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#b8860b]" />
                  Send E-Signature Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase">Document Type</label>
                  <p className="text-white mt-1">{esignDocType?.replace(/_/g, ' ').toUpperCase()}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase">Recipient Email</label>
                  <Input
                    type="email"
                    value={esignEmail}
                    onChange={(e) => setEsignEmail(e.target.value)}
                    placeholder="signer@example.com"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
                <div className="p-3 bg-[#0a1628] rounded-lg text-xs text-gray-400">
                  <p>An email will be sent to the recipient with a link to sign the document electronically.</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowEsignModal(false); setEsignDocData(null); }} 
                    className="flex-1 border-[#1a2d4a]"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={sendEsignatureRequest}
                    disabled={sendingEsign || !esignEmail}
                    className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                  >
                    {sendingEsign ? 'Sending...' : 'Send Request'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreditEnhancement;
